import { useEffect, useState, useCallback, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Search, LayoutGrid, List } from 'lucide-react';
import { startOfWeek, endOfWeek, isSameMonth, addMonths, addWeeks } from 'date-fns';
import { Meeting } from '@/types/Meeting';
import { getMeetings } from '@/api/meetings';
import { NewMeetingDialog } from '@/components/NewMeetingDialog';
import { MeetingCard } from '@/components/MeetingCard';
import { MeetingListItem } from '@/components/MeetingListItem';
import debounce from 'lodash.debounce';
import { useAuth } from '@/contexts/AuthContext';
import { getUserInfo } from '@/api/auth';

export function Meetings() {
  const { subscription, lastSubscriptionChangeTime, checkSubscriptionStatus } = useAuth();
  const [isFreeUser, setIsFreeUser] = useState<boolean | null>(null);
  
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [currentUserEmail, setCurrentUserEmail] = useState<string>('');
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list'); // Always default to list
  const observerRef = useRef<HTMLDivElement>(null);

  // Get current user's email
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const userInfo = await getUserInfo();
        setCurrentUserEmail(userInfo.email);
      } catch (error) {
        console.error('Error fetching user info:', error);
      }
    };
    
    fetchUserInfo();
  }, []);

  // Check for recent subscription changes when the component mounts or lastSubscriptionChangeTime changes
  useEffect(() => {
    const checkForSubscriptionChanges = async () => {
      // If there's a recent subscription change, force a refresh
      if (lastSubscriptionChangeTime > 0) {
        try {
          const latestStatus = await checkSubscriptionStatus(true);
          setIsFreeUser(latestStatus.status === 'free');
        } catch (error) {
          console.error('Error refreshing subscription status:', error);
          // Fallback to using the cached value
          setIsFreeUser(subscription?.status === 'free' || false);
        }
      } else if (subscription) {
        // Use the cached value if no recent changes
        setIsFreeUser(subscription.status === 'free');
      } else {
        // Default to non-free if no subscription data yet
        setIsFreeUser(false);
      }
    };
    
    checkForSubscriptionChanges();
  }, [lastSubscriptionChangeTime, subscription, checkSubscriptionStatus]);

  const fetchMeetings = useCallback(async () => {
    if (isLoading || !hasMore) return;
    setIsLoading(true);

    try {
      const response = await getMeetings(page,25, search);
      setMeetings((prevMeetings) => {
        const uniqueMeetings = [
          ...new Map([...prevMeetings, ...response.meetings].map((m) => [m.id, m])).values(),
        ];
        return uniqueMeetings;
      });
      setHasMore(response.meetings.length === 25);
    } catch (error) {
      console.error('Error fetching meetings:', error);
    } finally {
      setIsLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    fetchMeetings();
  }, [fetchMeetings]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading) {
          setPage((prevPage) => prevPage + 1);
        }
      },
      {
        root: null,
        rootMargin: '200px',
        threshold: 0.5,
      }
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => {
      if (observerRef.current) {
        observer.unobserve(observerRef.current);
      }
    };
  }, [hasMore, isLoading]);

  const handleSearchChange = debounce((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setMeetings([]);
    setPage(1);
    setHasMore(true);
  }, 300);

  const groupMeetings = (meetings: Meeting[]) => {
    const groups: { [key: string]: Meeting[] } = {
      'Later': [],
      'Next month': [],
      'Later this month': [],
      'Next week': [],
      'This week': [],
      'Last week': [],
      'Earlier this month': [],
      'Last month': [],
      'Older': [],
    };

    const now = new Date();
    now.setHours(0, 0, 0, 0);

    const thisWeekStart = startOfWeek(now, { weekStartsOn: 1 });
    const thisWeekEnd = endOfWeek(now, { weekStartsOn: 1 });
    const nextWeekStart = addWeeks(thisWeekStart, 1);
    const nextWeekEnd = endOfWeek(nextWeekStart, { weekStartsOn: 1 });
    const nextMonthStart = addMonths(new Date(now.getFullYear(), now.getMonth(), 1), 1);
    const nextMonthEnd = addMonths(nextMonthStart, 1);
    const endOfThisMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    meetings.forEach((meeting) => {
      const date = new Date(meeting.date);
      date.setHours(0, 0, 0, 0);

      if (date > nextMonthEnd) {
        groups['Later'].push(meeting);
      } else if (isSameMonth(date, nextMonthStart)) {
        groups['Next month'].push(meeting);
      } else if (date > nextWeekEnd && date <= endOfThisMonth) {
        groups['Later this month'].push(meeting);
      } else if (date >= nextWeekStart && date <= nextWeekEnd) {
        groups['Next week'].push(meeting);
      } else if (date >= thisWeekStart && date <= thisWeekEnd) {
        groups['This week'].push(meeting);
      } else if (date >= addWeeks(thisWeekStart, -1) && date <= addWeeks(thisWeekEnd, -1)) {
        groups['Last week'].push(meeting);
      } else if (isSameMonth(date, now)) {
        groups['Earlier this month'].push(meeting);
      } else if (isSameMonth(date, addMonths(now, -1))) {
        groups['Last month'].push(meeting);
      } else {
        groups['Older'].push(meeting);
      }
    });

    Object.keys(groups).forEach((key) => {
      groups[key].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    });

    return groups;
  };

  const groupOrder = [
    'Later',
    'Next month',
    'Later this month',
    'Next week',
    'This week',
    'Last week',
    'Earlier this month',
    'Last month',
    'Older',
  ];

  const groupedMeetings = groupMeetings(meetings);

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0 gap-4">
        <div className="w-full md:w-96">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none z-10" />
            <Input
              placeholder="Search meetings..."
              className="pl-8 w-full"
              onChange={handleSearchChange}
              autoFocus
            />
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {/* View Toggle */}
          <div className="flex bg-muted/60 p-1 rounded-lg border border-border backdrop-blur-sm">
            <button
              onClick={() => setViewMode('list')}
              className={`p-1.5 rounded-md transition-all ${
                viewMode === 'list'
                  ? 'bg-background text-primary shadow-sm'
                  : 'text-muted-foreground hover:bg-background/60 hover:text-foreground'
              }`}
              title="List view"
            >
              <List className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`p-1.5 rounded-md transition-all ${
                viewMode === 'grid'
                  ? 'bg-background text-primary shadow-sm'
                  : 'text-muted-foreground hover:bg-background/60 hover:text-foreground'
              }`}
              title="Grid view"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
          </div>

          {!isFreeUser && <NewMeetingDialog />}
        </div>
      </div>

      <div className="space-y-6">
        {groupOrder.map((group) =>
          groupedMeetings[group].length > 0 ? (
            <div key={group} className="space-y-3">
              <h2 className="text-lg font-semibold text-foreground">{group}</h2>
              
              {/* List View */}
              {viewMode === 'list' && (
                <div className="space-y-2">
                  {groupedMeetings[group].map((meeting) => (
                    <MeetingListItem
                      key={meeting.id}
                      meeting={meeting}
                    />
                  ))}
                </div>
              )}

              {/* Grid View */}
              {viewMode === 'grid' && (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {groupedMeetings[group].map((meeting) => (
                    <MeetingCard
                      key={meeting.id}
                      meeting={meeting}
                      currentUserEmail={currentUserEmail}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : null
        )}
        {isLoading && <div className="text-center">Loading...</div>}
        {!hasMore && <div className="text-center text-muted-foreground">No more meetings to load.</div>}
        <div ref={observerRef} />
      </div>
    </div>
  );
}
